<script setup>
/**
 * Authoring form for a WhatsApp message template.
 *
 * The header is text only: a media header needs an upload handle from Meta's resumable upload API,
 * which a WhatsApp Cloud inbox holds no credentials for. Button types are limited to the ones the
 * composer can actually send, so an admin cannot author a template the composer would then refuse.
 *
 * The parent unmounts this form after a successful submit, which is what resets it.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';

defineProps({
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;
const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];

const form = ref({
  name: '',
  category: 'MARKETING',
  language: 'en',
  header: '',
  headerExample: '',
  body: '',
  footer: '',
});
const exampleValues = ref({});
const buttons = ref([]);

const variablesIn = text => [
  ...new Set([...text.matchAll(VARIABLE_PATTERN)].map(match => match[1])),
];

// Meta needs one sample value per placeholder, or it rejects the submission outright.
const variables = computed(() => variablesIn(form.value.body));
const hasHeaderVariable = computed(
  () => variablesIn(form.value.header).length > 0
);

const categoryOptions = computed(() =>
  ['MARKETING', 'UTILITY'].map(value => ({
    value,
    label: t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${value}`),
  }))
);

const buttonTypeOptions = computed(() =>
  BUTTON_TYPES.map(value => ({
    value,
    label: t(`INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TYPE.${value}`),
  }))
);

const addButton = () =>
  buttons.value.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phoneNumber: '',
    example: '',
  });

const removeButton = index => buttons.value.splice(index, 1);

const buttonPayload = button => {
  switch (button.type) {
    case 'URL':
      return {
        type: button.type,
        text: button.text,
        url: button.url,
        example: button.example,
      };
    case 'PHONE_NUMBER':
      return {
        type: button.type,
        text: button.text,
        phone_number: button.phoneNumber,
      };
    case 'COPY_CODE':
      return { type: button.type, example: button.example };
    default:
      return { type: button.type, text: button.text };
  }
};

// The optional components are left out entirely when untouched, so an empty footer never reaches
// Meta as a blank one.
const submit = () => {
  const { name, category, language, body, header, headerExample, footer } =
    form.value;
  const payload = {
    name,
    category,
    language,
    body,
    example_values: variables.value.map(key => exampleValues.value[key] || ''),
  };

  if (header) payload.header = { text: header, example: headerExample };
  if (footer) payload.footer = footer;
  if (buttons.value.length) payload.buttons = buttons.value.map(buttonPayload);

  emit('submit', payload);
};
</script>

<template>
  <div
    class="flex flex-col p-4 border rounded-lg border-n-weak bg-n-solid-2"
    data-testid="template-form"
  >
    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.HELP_TEXT')"
    >
      <Input
        v-model="form.name"
        data-testid="template-name"
        :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.PLACEHOLDER')"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.HELP_TEXT')"
    >
      <Select
        v-model="form.category"
        :options="categoryOptions"
        data-testid="template-category"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE.HELP_TEXT')"
    >
      <Input
        v-model="form.language"
        placeholder="en"
        data-testid="template-language"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.HELP_TEXT')"
    >
      <Input
        v-model="form.header"
        data-testid="template-header"
        :placeholder="
          t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.PLACEHOLDER')
        "
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="hasHeaderVariable"
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_EXAMPLE.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARIABLE.HELP_TEXT')"
    >
      <Input
        v-model="form.headerExample"
        data-testid="template-header-example"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.HELP_TEXT')"
    >
      <TextArea
        v-model="form.body"
        data-testid="template-body"
        :max-length="1024"
        :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.PLACEHOLDER')"
        auto-height
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-for="variable in variables"
      :key="variable"
      :label="
        t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARIABLE.LABEL', {
          index: variable,
        })
      "
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.VARIABLE.HELP_TEXT')"
    >
      <Input
        v-model="exampleValues[variable]"
        :data-testid="`template-variable-${variable}`"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.HELP_TEXT')"
    >
      <Input
        v-model="form.footer"
        data-testid="template-footer"
        :placeholder="
          t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER.PLACEHOLDER')
        "
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.LABEL')"
      :help-text="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.HELP_TEXT')"
    >
      <div class="flex flex-col gap-3">
        <div
          v-for="(button, index) in buttons"
          :key="index"
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
        >
          <div class="flex items-start justify-between gap-2">
            <Select
              v-model="button.type"
              :options="buttonTypeOptions"
              :data-testid="`button-type-${index}`"
            />
            <Button
              :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.REMOVE')"
              size="sm"
              variant="ghost"
              color="ruby"
              :data-testid="`remove-button-${index}`"
              @click="removeButton(index)"
            />
          </div>

          <Input
            v-if="button.type !== 'COPY_CODE'"
            v-model="button.text"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TEXT')"
            :data-testid="`button-text-${index}`"
          />

          <Input
            v-if="button.type === 'URL'"
            v-model="button.url"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.URL')"
            placeholder="https://example.com/{{1}}"
            :data-testid="`button-url-${index}`"
          />

          <Input
            v-if="button.type === 'PHONE_NUMBER'"
            v-model="button.phoneNumber"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.PHONE')"
            placeholder="+15550001111"
            :data-testid="`button-phone-${index}`"
          />

          <Input
            v-if="
              button.type === 'COPY_CODE' ||
              (button.type === 'URL' && variablesIn(button.url).length)
            "
            v-model="button.example"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.EXAMPLE')"
            :data-testid="`button-example-${index}`"
          />
        </div>

        <Button
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.ADD')"
          size="sm"
          variant="outline"
          color="slate"
          class="w-fit"
          data-testid="add-button"
          @click="addButton"
        />
      </div>
    </SettingsFieldSection>

    <div class="flex justify-end">
      <Button
        :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.SUBMIT')"
        size="sm"
        :is-loading="isSubmitting"
        :disabled="isSubmitting || !form.name || !form.body"
        data-testid="submit-template"
        @click="submit"
      />
    </div>
  </div>
</template>
